import { Component, Input, OnChanges, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ModalWizardComponent, ModalWizardPageComponent, ScApiService, ScuiModalComponent } from '@sc-ui';
import Chart from 'chart.js/auto';
import { DemoService } from 'src/services/demo.service';
import { LocalizationService } from 'src/services/localization.service';
import { demoPeople } from '../../pool/pool.demo';
import { texts } from './project-analysis.texts';
import { UserService } from 'src/services/user.service';

@Component({
  standalone: false,
  selector: 'app-project-analysis',
  templateUrl: './project-analysis.component.html',
  styleUrls: ['./project-analysis.component.scss']
})
export class ProjectAnalysisComponent implements OnChanges {

  texts = texts;

  @Input()
  project: any;

  analysis: any;

  isLoading = true;

  candidates: any;

  @ViewChild('reviewModal')
  reviewModal: ModalWizardComponent;

  @ViewChild('reviewConfirmation')
  reviewConfirmation: ModalWizardPageComponent;

  @ViewChild('analysisAlreadyRequestedConfirmation')
  analysisAlreadyRequestedConfirmation: ModalWizardPageComponent;

  private revealTimeouts: any[] = [];

  @ViewChild('editRequirementsModal')
  editRequirementsModal: ScuiModalComponent;

  selectedRequirement: any;

  editRequirements = false;

  experienceChart: any;

  // Hardcoded for now: Köln with a 50 km radius. Bind project.Profile.Locations
  // here once the analysis delivers its own coordinates.
  mapLocations: any = [
    { Latitude: 50.937531, Longitude: 6.960279, Radius: 50 }
  ];

  isRequestingNewAnalysis = false;

  // a requested analysis is the project's status until the analysis is sent
  get analysisRequested() {
    return this.project?.Status == 'AnalysisRequested';
  }

  // the pool card only renders in demo mode, so its matches are hardcoded
  poolMatches: any = demoPeople;

  constructor(private api: ScApiService,
    public loc: LocalizationService,
    public demo: DemoService,
    public userService: UserService,) { }

  ngOnChanges() {


    if (!this.project) return;

    // the list brings the reference profiles' headers, the review needs them in full
    this.api.get('projects/' + this.project.Id + '/candidates?demoProfiles=true').subscribe((d: any) => {
      this.candidates = d;
      this.candidates.forEach(c => this.loadProfile(c));
    });

    this.api.get('projects/' + this.project.Id + '/analysis').subscribe((d: any) => {
      this.analysis = d.Result;
      this.isLoading = false;
      setTimeout(() => this.renderExperienceChart());
    });
  }

  // Hardcoded for now: the share of matching talents over years of experience,
  // with the requested band highlighted.
  private renderExperienceChart() {

    if (this.experienceChart) return;
    if (!document.getElementById('experienceChart')) return;

    var labels = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'];
    var distribution = [0, 3, 9, 19, 32, 45, 57, 68, 77, 84, 90, 94, 97, 98, 99, 100];

    var acceptedFrom = 3;
    var acceptedTo = 10;
    var accepted = labels.map((l, i) => i >= acceptedFrom && i <= acceptedTo ? 100 : null);

    this.experienceChart = new Chart('experienceChart', {
      data: {
        labels: labels,
        datasets: [
          {
            type: 'bar',
            label: 'Akzeptiert: ' + acceptedFrom + '–10 Jahre',
            data: accepted,
            backgroundColor: 'rgba(31,194,103,0.12)',
            borderWidth: 0,
            categoryPercentage: 1,
            barPercentage: 1,
            order: 2
          },
          {
            type: 'line',
            label: 'Anteil passender Talente',
            data: distribution,
            borderColor: 'rgba(0,98,215,1)',
            backgroundColor: 'rgba(0,98,215,0.08)',
            borderWidth: 2,
            pointRadius: 0,
            tension: .35,
            fill: true,
            order: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        scales: {
          x: {
            title: { display: true, text: 'Jahre Berufserfahrung' },
            grid: { display: false }
          },
          y: {
            min: 0,
            max: 100,
            ticks: { callback: (value) => value + '%' }
          }
        },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              label: (context) => context.dataset.type == 'bar'
                ? context.dataset.label
                : context.dataset.label + ': ' + context.parsed.y + '%'
            }
          }
        }
      }
    });
  }

  canReview() {
    return this.project?.Status == 'AwaitingOrder';
  }

  // a profile opens its own page, the card footer starts at the first one
  // The review modal holds a page per profile and would render them all at once,
  // keeping the main thread (and the scrolling) busy right after it opens. So the
  // opened profile renders immediately and the others follow a second apart,
  // in reading order from it, unless they are navigated to first.
  showReview(index = 0) {

    this.revealTimeouts.forEach(t => clearTimeout(t));
    this.revealTimeouts = [];

    this.candidates[index].isVisible = true;

    // the wizard's first page is laid out in flow and gives the modal its height,
    // the others are pinned over it; empty, it would open the modal collapsed
    this.candidates[0].isVisible = true;

    for (var step = 1; step < this.candidates.length; step++) {
      // let, so each timeout keeps its own candidate
      let candidate = this.candidates[(index + step) % this.candidates.length];
      this.revealTimeouts.push(setTimeout(() => candidate.isVisible = true, step * 1000));
    }

    this.reviewModal.show(this.reviewModal.getPage(index));
  }

  // fetches the full profile into the list's candidate
  private loadProfile(candidate: any) {
    this.api.get(`projects/${this.project.Id}/candidates/${candidate.Id}`).subscribe((profile: any) => {
      this.mergeProfile(candidate, profile);
    });
  }

  // The full profile goes without what the list already has: it leaves out the
  // links of a full project's profiles, and its assignment has fewer fields. So
  // empty values don't overwrite, and objects are merged field by field.
  private mergeProfile(candidate: any, profile: any) {
    for (var key in profile) {
      var value = profile[key];
      if (value == null) continue;

      var isObject = typeof value == 'object' && !Array.isArray(value);
      candidate[key] = isObject && candidate[key] ? Object.assign({}, candidate[key], value) : value;
    }
  }

  removeError(candidate) {
    candidate.isCommentRequired = false;
  }

  // the review of a reference profile: the comment, posted to the profile's feed
  // and put on top of it
  submitComment(candidate: any, feed: any) {

    candidate.isSendingComment = true;

    var activity = {
      Content: 'candidate_reference_review',
      Payload: {
        Body: candidate.commentInput
      }
    };

    this.api.post(feed.uri, activity).subscribe(d => {
      candidate.isSendingComment = false;
      candidate.commentInput = '';
      candidate.isCommentRequired = false;
      feed.activities.splice(0, 0, d);
      // the feed reports its items once they're loaded, which may not have happened
      candidate.feedActivities = feed.activities;
    });
  }

  acceptReference(candidate: any, index: number) {
    this.postReferenceVerdict(candidate, 'candidate_reference_accepted');
    this.nextReview(index);
  }

  // a rejection needs a comment on what doesn't fit, by anyone; the profile's feed
  // already holds its activities (candidate.feedActivities), so no request is needed
  rejectReference(candidate: any, index: number) {

    var hasComment = (candidate.feedActivities ?? [])
      .some(a => a.Content == '#commented' || a.Content == 'candidate_reference_review');

    if (!hasComment) {
      candidate.isCommentRequired = true;
      return;
    }

    this.postReferenceVerdict(candidate, 'candidate_reference_rejected');
    this.nextReview(index);
  }

  // the verdict goes to the profile's feed, the review doesn't wait for it
  private postReferenceVerdict(candidate: any, content: string) {
    this.api.post(`projects/${this.project.Id}/candidates/${candidate.Id}/activities`, { Content: content })
      .subscribe(d => candidate.feedActivities?.splice(0, 0, d));
  }

  previousReview(index: number) {
    this.candidates[index - 1].isVisible = true;
    this.reviewModal.previous();
  }

  // after the last profile the review ends on a confirmation, which doesn't offer
  // a new analysis when one is already requested
  nextReview(index: number) {
    if (index < this.candidates.length - 1) {
      this.candidates[index + 1].isVisible = true;
      this.reviewModal.next();
      return;
    }

    this.reviewModal.navigateTo(this.analysisRequested ? this.analysisAlreadyRequestedConfirmation : this.reviewConfirmation);
  }

  // asks for a new analysis after the review, as app-modal-request-analysis does; the
  // existing analysis stays on display until the new one replaces it
  requestNewAnalysis(confirmationPage: any) {

    if (this.isRequestingNewAnalysis) return;

    var confirm = () => {
      this.project.Status = 'AnalysisRequested';
      this.reviewModal.navigateTo(confirmationPage);
    };

    // demo requests are only confirmed, so the project is unchanged after a reload
    if (this.demo.isActive) {
      confirm();
      return;
    }

    this.isRequestingNewAnalysis = true;

    this.api.post(`projects/${this.project.Id}/request-analysis`, {}).subscribe(() => {
      this.isRequestingNewAnalysis = false;
      confirm();
    });
  }

  scoreRating(company: any): 'good' | 'neutral' | 'bad' {
    const diff = (company?.Score || 0) - (company?.IndustryAverageScore || 0);
    if (diff >= 0.1) return 'good';
    if (diff <= -0.1) return 'bad';
    return 'neutral';
  }

  ratingBadge(rating: string) {
    return rating === 'good' ? 'badge-success-soft' : rating === 'bad' ? 'badge-danger-soft' : 'badge-warning-soft';
  }


  startEdit() {
    if (this.editRequirements) return;
    this.editRequirementsModal.show();
    return;
  }

  save() {
    this.api.put('projects', this.project).subscribe();
  }
}
